import Navigation from "@/components/Navigation";
import Breadcrumbs from "@/components/Breadcrumbs";
import Footer from "@/components/Footer";
import { Card } from "@/components/ui/card";
import { CheckCircle2, Globe, Users, Zap, TrendingUp } from "lucide-react";
import { useSEO } from "@/hooks/useSEO";

// Import client logos
import lornaJaneLogo from "@/assets/clients/lorna-jane.png";
import ringersWesternLogo from "@/assets/clients/ringers-western.png";
import universalCoinLogo from "@/assets/clients/universal-coin.png";

// Import location images
import seattleImage from "@/assets/locations/seattle.jpg";
import vancouverImage from "@/assets/locations/vancouver.jpg";
import brisbaneImage from "@/assets/locations/brisbane.jpg";
import michiganImage from "@/assets/locations/michigan.jpg";
import jordanImage from "@/assets/locations/jordan.jpg";
import londonImage from "@/assets/locations/london.jpg";

const AboutUs = () => {
  useSEO({
    title: 'About Ommni - Powering the Engine Room of Commerce',
    description: 'Learn about Ommni, the Commerce Operations Platform unifying Product, Pricing, and Inventory management for modern retailers. Trusted by Lorna Jane, Ringers Western, and Universal Coin.',
    keywords: 'about ommni, commerce operations platform, PIM platform, product information management, retail technology company, ecommerce software, inventory management company',
    canonical: 'https://ommni.io/about',
  });

  const features = [
    {
      icon: Zap,
      title: "Complex Product Data",
      description: "We handle multi-level product hierarchies with ease."
    },
    {
      icon: TrendingUp,
      title: "Unified System",
      description: "Bringing disjointed spreadsheets into one intelligent platform."
    },
    {
      icon: Globe,
      title: "Global Scaling",
      description: "Advanced inventory and pricing control across regions and channels."
    },
    {
      icon: Users,
      title: "No-Code Automation",
      description: "OmmniFlow lets you configure workflows without writing code."
    }
  ];

  const clients = [
    { name: "Lorna Jane", logo: lornaJaneLogo },
    { name: "Ringers Western", logo: ringersWesternLogo },
    { name: "Universal Coin & Bullion", logo: universalCoinLogo },
  ];

  const locations = [
    { name: "Seattle, USA", image: seattleImage },
    { name: "Vancouver, Canada", image: vancouverImage },
    { name: "Brisbane, Australia", image: brisbaneImage },
    { name: "Michigan, USA", image: michiganImage },
    { name: "Amman, Jordan", image: jordanImage },
    { name: "London, United Kingdom", image: londonImage },
  ];

  return (
    <div className="min-h-screen bg-background" itemScope itemType="https://schema.org/AboutPage">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 overflow-hidden" aria-label="About Ommni hero section">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-background to-background" aria-hidden="true" />
        <div className="container mx-auto max-w-6xl relative z-10">
          <div className="text-center space-y-6">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold leading-tight" itemProp="headline">
              About Ommni
            </h1>
            <p className="text-2xl md:text-3xl lg:text-4xl text-primary font-semibold">
              Powering the Engine Room of Commerce
            </p>
            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
              Redefining how commerce operations are built, scaled, and managed
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <main className="px-4 pb-12">
        <div className="container mx-auto max-w-6xl space-y-24">
          
          {/* Introduction */}
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h2 className="text-3xl md:text-4xl font-bold">
                More Than Just a PIM
              </h2>
              <div className="space-y-4 text-lg text-muted-foreground">
                <p>
                  Modern retail isn't just about selling products — it's about intelligently managing the data that powers every product, price, and inventory decision across every channel.
                </p>
                <p>
                  We're your <span className="text-primary font-semibold">Commerce Operations Platform</span> — a unified hub where Product, Pricing, and Inventory come together to define, display, and distribute your entire product catalogue with precision.
                </p>
              </div>
            </div>
            <Card className="p-8 gradient-card border-2 hover:border-primary/50 transition-all duration-300">
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <CheckCircle2 className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <p className="text-base">D2C storefronts and marketplaces</p>
                </div>
                <div className="flex items-start gap-4">
                  <CheckCircle2 className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <p className="text-base">B2B sales channels</p>
                </div>
                <div className="flex items-start gap-4">
                  <CheckCircle2 className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <p className="text-base">In-store POS systems</p>
                </div>
                <div className="flex items-start gap-4">
                  <CheckCircle2 className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <p className="text-base">Automated workflows at scale</p>
                </div>
              </div>
            </Card>
          </div>

          {/* Features Grid */}
          <div>
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Built for Modern Commerce Teams
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Solving the day-to-day pain points that eCommerce, retail ops, and digital teams face
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {features.map((feature, index) => (
                <Card 
                  key={index} 
                  className="p-6 gradient-card border-2 hover:border-primary/50 transition-all duration-300 group"
                >
                  <div className="space-y-4">
                    <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                      <feature.icon className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="text-lg font-bold">{feature.title}</h3>
                    <p className="text-sm text-muted-foreground">{feature.description}</p>
                  </div>
                </Card>
              ))}
            </div>
            <Card className="mt-8 p-8 gradient-card text-center">
              <p className="text-lg">
                Whether you're replacing a legacy PIM, scaling beyond spreadsheets, or bridging the gap between your WMS, ERP, and eComm stack — 
                <span className="text-primary font-semibold"> Ommni is the operational core you've been waiting for.</span>
              </p>
            </Card>
          </div>

          {/* Trusted Brands */}
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent rounded-3xl" />
            <div className="relative p-8 md:p-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-center">
                Trusted by Leading Brands
              </h2>
              <p className="text-lg text-muted-foreground mb-12 text-center max-w-3xl mx-auto">
                Supporting high-growth and high-scale retailers across fashion, beauty, CPG, and specialty retail
              </p>
              
              <div className="grid md:grid-cols-3 gap-8 mb-12">
                {clients.map((client, index) => (
                  <Card
                    key={index}
                    className="p-8 bg-background/80 backdrop-blur-sm border-2 hover:border-primary/50 transition-all duration-300 hover:shadow-xl group"
                  >
                    <div className="flex items-center justify-center h-20">
                      <img
                        src={client.logo}
                        alt={`${client.name} - Trusted Ommni commerce operations platform client`}
                        className="h-full w-auto object-contain group-hover:scale-110 transition-transform duration-300"
                        loading="lazy"
                      />
                    </div>
                  </Card>
                ))}
              </div>

              <p className="text-center text-muted-foreground text-lg">
                These brands use Ommni to centralise their product data, streamline inventory logic, and automate syndication across <span className="text-primary font-semibold">Shopify, NetSuite, PeopleVox, Amazon</span>, and beyond.
              </p>
            </div>
          </div>

          {/* Global Presence */}
          <div>
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Our Global Presence
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                With team members spanning across the globe, we serve clients worldwide from key innovation hubs
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {locations.map((location, index) => (
                <Card
                  key={index}
                  className="group relative overflow-hidden border-2 hover:border-primary/50 transition-all duration-300 hover:shadow-2xl"
                >
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={location.image}
                      alt={`Ommni office location in ${location.name} - Global commerce operations team presence`}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      loading="lazy"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent opacity-90" />
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <h3 className="text-xl font-bold text-foreground">
                      {location.name}
                    </h3>
                  </div>
                </Card>
              ))}
            </div>
          </div>

          {/* Future Vision */}
          <div className="relative">
            <Card className="p-12 md:p-16 bg-card text-center border-2 hover:border-primary/50 transition-all duration-300">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-foreground">
                The Future is Unified
              </h2>
              <div className="max-w-3xl mx-auto space-y-6 text-lg text-muted-foreground">
                <p>
                  The commerce tech stack is only getting more complex. At Ommni, we're committed to helping retailers unify their operations — not just through PIM, but across the full lifecycle of commerce data.
                </p>
                <p className="text-primary font-semibold">
                  Our roadmap continues to expand into order management, inventory intelligence, and deeper workflow orchestration.
                </p>
                <p>
                  All powered by the same flexible architecture that made Ommni the go-to platform for modern commerce teams.
                </p>
              </div>
            </Card>
          </div>

        </div>
      </main>

      <div className="pb-24">
        <Breadcrumbs />
        <Footer />
      </div>
    </div>
  );
};

export default AboutUs;
